import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { Account, AccountBalance, AccountType, Currency, ListResponse } from './types'

const ROOT = '/accounts'
const KEY = ['accounts'] as const

export function useAccounts(filter: { organizationId?: string | null } = {}) {
  const api = useApi()
  const orgId = filter.organizationId ?? null
  return useQuery({
    queryKey: [...KEY, { organizationId: orgId }],
    queryFn: () =>
      api<ListResponse<Account>>(
        `${ROOT}${buildQuery({ organization_id: orgId ?? undefined })}`,
      ).then((r) => r.items),
  })
}

export function useAccountBalance(accountId: string | null | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: ['account-balance', accountId],
    enabled: !!accountId,
    queryFn: () => api<AccountBalance>(`${ROOT}/${accountId}/balance`),
  })
}

export function useAccountBalances(filter: { organizationId?: string | null } = {}) {
  const api = useApi()
  const orgId = filter.organizationId ?? null
  return useQuery({
    queryKey: ['account-balance', 'list', { organizationId: orgId }],
    queryFn: () =>
      api<ListResponse<AccountBalance>>(
        `${ROOT}/balances${buildQuery({ organization_id: orgId ?? undefined })}`,
      ).then((r) => r.items),
  })
}

export type CreateAccountInput = {
  organizationId: string
  name: string
  type: AccountType
  currency: Currency
  openingBalance?: number
  openingBalanceDate: string
}

export function useCreateAccount() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAccountInput) =>
      api<Account>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type UpdateAccountInput = {
  id: string
  patch: Partial<{
    name: string
    type: AccountType
    currency: Currency
    openingBalance: number
    openingBalanceDate: string
    isArchived: boolean
  }>
}

export function useUpdateAccount() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateAccountInput) =>
      api<Account>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['account-balance', variables.id] })
    },
  })
}

export function useDeleteAccount() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
